import React from 'react';
import moment from 'moment-timezone';
import {
  Button,
  Calendar,
  CalendarPermissionsUtil,
  CalendarTimeZoneUtil,
  CalendarEventsUtil,
  EVENT_STATES,
  Form,
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  useApi,
  Grid,
  GridRow,
  GridColumn,
  Paragraph,
  Gutter,
  TimezoneDropdown,
  Well,
  SectionHeader,
  useBreakpoint,
  Accolade,
  Icon,
} from '@axiom/ui';
import type {
  Calendar as CalendarType,
  CalendarEventType,
  Candidate,
  TimeZone,
} from '@axiom/validation';
import { NO_TIMEZONE, PermissionImpersonationRoles } from '@axiom/const';

import { CandidateApi } from '../../api/candidate-api';
import { CalendarApi } from '../../api/calendar-api';
import { UserApi } from '../../api/user-api';
import {
  InterviewAvailabilityConst,
  InterviewAvailabilityUtil,
} from '../../utils/interview-availability-util';

import { InterviewAvailabilityShareableFormSchema } from './interview-availability-shareable-form-schema';

const { minimumHoursRequired } = InterviewAvailabilityConst;
const { calculateAvailabilityWithinNextTwoWeeks } = InterviewAvailabilityUtil;

const dayWeekMode = {
  slots: 4,
  increments: 15,
};

export type InterviewAvailabilityShareableProps = {
  candidateId: Candidate['id'];
  message?: string;
  name?: string;
  title: string;
  onAfterSubmit: () => Promise<void>;
  onClose: () => Promise<void>;
  showSelfService?: boolean;
  submitButtonText?: string;
};

export const InterviewAvailabilityShareable = ({
  candidateId,
  message,
  name = 'INTERVIEW_AVAILABILITY_SHAREABLE',
  title,
  onAfterSubmit,
  onClose,
  showSelfService = false,
  submitButtonText = 'Submit',
}: InterviewAvailabilityShareableProps) => {
  const { isMobile } = useBreakpoint();

  // ASYNC DATA
  const [{ data: user }, { data: candidate }] = useApi(
    UserApi.readUser(),
    CandidateApi.readCandidate(candidateId)
  );
  const { id: calendarId = null, timezone: talentIana } =
    candidate.calendar || {};
  const [
    { data: calendarData } = {
      data: {
        freeTimeUpdatedAt: null,
        freeTimeUpdatedUserType: null,
      } as Partial<CalendarType>,
    },
  ] = useApi(calendarId ? CalendarApi.readCalendar(calendarId) : undefined);

  // TIMEZONE
  const guessIana = moment.tz.guess();
  const timezone = CalendarTimeZoneUtil.getTimeZone(talentIana || guessIana);

  // PERMISSIONS
  const { userPermissions } = CalendarPermissionsUtil(
    user,
    PermissionImpersonationRoles.talent
  );
  const calendarPermissions = userPermissions();

  const initialCalendarEvents = (calendarData.events || []).map(e => ({
    accountName: e.accountName,
    attendees: e.attendees,
    busy: e.busy,
    endTime: e.endTime,
    error: e.error,
    id: e.id,
    name: e.name,
    startTime: e.startTime,
    title: e.title,
    state: e.state ?? EVENT_STATES.SAVED,
  }));

  return (
    <Form
      name={name}
      schema={InterviewAvailabilityShareableFormSchema}
      initialValues={{
        CALENDAR_COMPONENT: initialCalendarEvents,
        TIMEZONE_DROPDOWN: timezone === NO_TIMEZONE ? undefined : timezone.id,
      }}
      onSubmit={async (changedData, actions) => {
        const { CALENDAR_COMPONENT, TIMEZONE_DROPDOWN } = changedData;
        const { setFieldError } = actions;

        const cleanEvents = CalendarEventsUtil.cleanAndOrganiseEvents({
          permissions: calendarPermissions,
          initData: initialCalendarEvents,
          changedEvents: CALENDAR_COMPONENT,
        });

        const { deleteIds, modifiedEvents, newEvents, staticEvents } =
          cleanEvents;

        const modifiedEventsIds = new Set(modifiedEvents.map(v => v.id));

        const filteredEvents = initialCalendarEvents.filter(
          event =>
            !deleteIds.includes(event.id) && !modifiedEventsIds.has(event.id)
        );

        const totalFreeTime =
          calculateAvailabilityWithinNextTwoWeeks(modifiedEvents) +
          calculateAvailabilityWithinNextTwoWeeks(newEvents) +
          calculateAvailabilityWithinNextTwoWeeks(staticEvents) +
          calculateAvailabilityWithinNextTwoWeeks(filteredEvents);

        if (totalFreeTime < minimumHoursRequired) {
          setFieldError(
            'CALENDAR_COMPONENT',
            'Please enter at least 8 hrs over the next 2 weeks'
          );
          return;
        }

        const { iana }: TimeZone = TIMEZONE_DROPDOWN
          ? CalendarTimeZoneUtil.getZoneFromId(TIMEZONE_DROPDOWN)
          : timezone;

        let currentCalendarId = calendarId;
        if (!currentCalendarId) {
          const newCalendar = await CalendarApi.createCalendar({
            candidateId: candidate.id,
            timezone: iana,
          });
          currentCalendarId = newCalendar.data.id;
        }
        const promises = [];

        // These API calls are in specific order to prevent race-conditions ==================== START
        if (cleanEvents.deleteIds.length > 0) {
          await CalendarApi.deleteCalendarEvents(
            currentCalendarId,
            cleanEvents.deleteIds
          );
        }

        if (cleanEvents.modifiedEvents.length > 0) {
          const payload = cleanEvents.modifiedEvents.reduce<{
            [key: string]: CalendarEventType;
          }>((crnt, event) => {
            if (event.id) {
              crnt[event.id] = {
                accountName: event.accountName,
                attendees: event.attendees,
                busy: event.busy,
                endTime: event.endTime,
                error: event.error,
                name: event.name,
                startTime: event.startTime,
                state: event.state,
                title: event.title,
              };
            }
            return crnt;
          }, {});

          promises.push(
            CalendarApi.updateCalendarEvents(currentCalendarId, payload)
          );
        }

        if (cleanEvents.newEvents.length > 0) {
          promises.push(
            CalendarApi.createCalendarEvents(
              currentCalendarId,
              cleanEvents.newEvents
            )
          );
        }

        await Promise.all(promises);

        if (TIMEZONE_DROPDOWN) {
          // API call not part of the Promise.all() above to avoid race-condition
          await CalendarApi.updateCalendar(currentCalendarId, {
            timezone: iana,
          });
        }

        await Promise.all([
          CalendarApi.refreshCalendar(calendarId),
          CalendarApi.refreshCalendarEvents(calendarId),
        ]);

        // API call not part of the Promise.all() above to avoid race-condition
        await CandidateApi.refreshCandidate(candidate.id);
        // These API calls are in specific order to prevent race-conditions ==================== END

        return onAfterSubmit();
      }}
    >
      {({ fireSubmit, values }) => {
        return (
          <Modal name="INTERVIEW_AVAILABILITY_SHAREABLE_MODAL" size="large">
            <ModalHeader
              name="PENDO_INTERVIEW_AVAILABILITY_SHAREABLE_MODAL_HEADER"
              onClose={async () => onClose()}
            >
              {title}
            </ModalHeader>
            <ModalSection>
              <Grid>
                <GridRow>
                  <GridColumn widescreenWidth={4} smallScreenWidth={12}>
                    {showSelfService ? (
                      <Gutter bottom="16px">
                        <Accolade
                          color="purple"
                          label="Self-service"
                          name="SELF_SERVICE_ACCOLADE"
                        >
                          <Icon name="star-filled" />
                        </Accolade>
                      </Gutter>
                    ) : null}
                    {!!message && (
                      <Gutter bottom="16px">
                        <Paragraph name="MESSAGE">{message}</Paragraph>
                      </Gutter>
                    )}
                    <Well background="blue" name="AVAILABILITY_WELL">
                      <SectionHeader name="SECTION_HEADER">
                        Enter your interview availability
                      </SectionHeader>
                      <Gutter bottom="8px" />
                      <Paragraph name="INSTRUCTIONS">
                        Select at least 8 hours of availability over the next 2
                        weeks. Clients will be able to pick from your
                        availability when requesting an interview with you.
                      </Paragraph>
                    </Well>
                    <Gutter bottom="16px" />
                    <TimezoneDropdown name="TIMEZONE_DROPDOWN" />
                    <Gutter bottom="16px" />
                  </GridColumn>
                  <GridColumn widescreenWidth={8} smallScreenWidth={12}>
                    <Calendar
                      name="CALENDAR_COMPONENT"
                      permissions={calendarPermissions}
                      dayMode={dayWeekMode}
                      weekMode={dayWeekMode}
                      timezone={
                        values.TIMEZONE_DROPDOWN
                          ? CalendarTimeZoneUtil.getZoneFromId(
                              values.TIMEZONE_DROPDOWN
                            ).iana
                          : timezone.iana
                      }
                    />
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <ModalFooter>
              <Button
                name="PENDO_INTERVIEW_AVAILABILITY_SHAREABLE_MODAL_CANCEL"
                onClick={async () => onClose()}
                pattern="primary"
                variation="outline"
              >
                Cancel
              </Button>
              {!isMobile && (
                <Button name="SUBMIT" onClick={fireSubmit}>
                  {submitButtonText}
                </Button>
              )}
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
